// Prose composition for the center panel of the triptych. Ports the daybook
// variant's compose-entry, narrowed: the wings carry chapter context and reviews,
// so center prose returns only date + paragraph (with inline glyph) + primary CTA.

import type { Lesson } from '@kanjido/database'

import { spellSmall } from '@/journey/_lib/word-utils'
import { REVIEW_SITTING_SIZE_RESTORATION } from '@/lib/srs/review-sitting-size'

/** The four practice paths, in progression order. */
export type PathType = 'learn' | 'review' | 'quiz' | 'challenge'
/** One run of composed prose: plain text or an inline kanji glyph. */
export type EntryPart =
    | { kind: 'text'; text: string }
    | { kind: 'glyph'; character: string; meaning: string }
/** The stand's formatted date heading. */
export interface DateLine {
    primary: string
}
/** The primary CTA rendered in the SignoffLine — verb plus target path. */
export interface SignoffPrimary {
    verb: string
    lessonNumber: number | null
    pathLabel: string
    href: string
}
/** The register the stand speaks in; selects the prose template. */
export type StandRegister =
    | 'session'
    | 'first-step'
    | 'resting'
    | 'edition-complete'
    | 'return'
    | 'return-resting'
/** The composed center panel of the dashboard triptych. */
export interface Stand {
    register: StandRegister
    dateLine: DateLine
    paragraph: EntryPart[]
    primary: SignoffPrimary | null
}
/** Inputs assembled by the dashboard loader for {@link composeStand}. */
export interface ComposeStandInput {
    today: Date
    nextAction: { lessonNumber: number; path: PathType } | null
    kanjiTouched: number
    /**
     * Real kanji with `kanji_progress.status = 'introduced'` — past Learn,
     * no FSRS rating yet. Kept separate from `kanjiTouched` (whose meaning
     * other surfaces depend on); the onboarding/frame-index math below
     * accounts for both.
     */
    kanjiIntroduced: number
    totalRealKanji: number
    lesson: Pick<Lesson, 'number' | 'title'> | null
    anchor: { character: string; meaning: string } | null
    restoration: {
        daysAway: number
        reviewsBacklog: number
        // Head of the global review queue — the first card the capped
        // session will actually serve. The restoration register is
        // de-lessoned (restoration mini-spec), so no lesson-scoped
        // anchor applies.
        anchor: { character: string; meaning: string } | null
    } | null
    reviewsDue: number
    /**
     * Cards in the review queue that were introduced and never rated —
     * a subset of `reviewsDue` (`reviewsFresh ≤ reviewsDue`). Named "new"
     * on the CTA so a finished lesson does not read as overdue work.
     */
    reviewsFresh: number
    /**
     * Highest lesson whose Learn path has been entered (`in_progress` or
     * `completed`). Position prose narrates from the frontier, not the
     * recommendation cursor — see Spec v1.6 §11.7 Decision 2.
     */
    frontierLessonNumber: number | null
}

const ONES = [
    'zero',
    'one',
    'two',
    'three',
    'four',
    'five',
    'six',
    'seven',
    'eight',
    'nine',
    'ten',
    'eleven',
    'twelve',
    'thirteen',
    'fourteen',
    'fifteen',
    'sixteen',
    'seventeen',
    'eighteen',
    'nineteen',
] as const
const TENS = [
    '',
    '',
    'twenty',
    'thirty',
    'forty',
    'fifty',
    'sixty',
    'seventy',
    'eighty',
    'ninety',
] as const
const ORD_ONES: Record<string, string> = {
    zero: 'zeroth',
    one: 'first',
    two: 'second',
    three: 'third',
    four: 'fourth',
    five: 'fifth',
    six: 'sixth',
    seven: 'seventh',
    eight: 'eighth',
    nine: 'ninth',
    ten: 'tenth',
    eleven: 'eleventh',
    twelve: 'twelfth',
    thirteen: 'thirteenth',
    fourteen: 'fourteenth',
    fifteen: 'fifteenth',
    sixteen: 'sixteenth',
    seventeen: 'seventeenth',
    eighteen: 'eighteenth',
    nineteen: 'nineteenth',
}
const ORD_TENS: Record<string, string> = {
    twenty: 'twentieth',
    thirty: 'thirtieth',
    forty: 'fortieth',
    fifty: 'fiftieth',
    sixty: 'sixtieth',
    seventy: 'seventieth',
    eighty: 'eightieth',
    ninety: 'ninetieth',
}
const PATH_LABEL: Record<PathType, string> = {
    learn: 'Learn',
    review: 'Review',
    quiz: 'Quiz',
    challenge: 'Challenge',
}
const PATH_VERB: Record<PathType, string> = {
    learn: 'Continue',
    review: 'Return',
    quiz: 'Sit',
    challenge: 'Stand',
}
/**
 * Practice-route href for a path. Review is ONE global queue at
 * /journey/review (SRS v2 spec §5) — there is no per-lesson review
 * route; every other path keeps its per-lesson practice route.
 */
export const hrefForPath = (lessonNumber: number, path: PathType): string =>
    path === 'review'
        ? '/journey/review'
        : `/journey/lessons/${lessonNumber}/${path}`
const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
]
const SUFFIX = [
    'th',
    'st',
    'nd',
    'rd',
    'th',
    'th',
    'th',
    'th',
    'th',
    'th',
] as const

function belowHundred(n: number): string {
    if (n < 20) return ONES[n]!
    const t = Math.floor(n / 10),
        o = n % 10
    return o === 0 ? TENS[t]! : `${TENS[t]}-${ONES[o]}`
}

function ordinalWord(n: number): string {
    if (n <= 0 || n > 100) return `${n}th`
    if (n === 100) return 'one hundredth'
    const w = belowHundred(n)
    if (ORD_ONES[w]) return ORD_ONES[w]
    if (ORD_TENS[w]) return ORD_TENS[w]
    if (w.includes('-')) {
        const [tens, ones] = w.split('-') as [string, string]
        return `${tens}-${ORD_ONES[ones] ?? `${ones}th`}`
    }
    return `${w}th`
}

function formatDate(d: Date): string {
    const day = d.getDate(),
        m = day % 100
    const suffix = m >= 11 && m <= 13 ? 'th' : SUFFIX[day % 10]!
    return `${MONTHS[d.getMonth()]!} ${day}${suffix}`
}

const compactCorpus = (n: number) =>
    n === 2200 ? 'two thousand two hundred' : n.toLocaleString('en-US')
const joinedFrameNumber = (n: number) =>
    n <= 100 ? `the ${ordinalWord(n)} frame` : `frame ${n}`
const text = (t: string): EntryPart => ({ kind: 'text', text: t })
const dueCountPhrase = (n: number) => spellSmall(n)

/**
 * The review CTA's path label: the queue's two populations named apart
 * (`Review · twelve due · forty new`), dashboard v1.8. `reviewsDue` is the
 * whole queue, so the scheduled-due segment is the remainder after fresh.
 */
export function formatReviewCountLabel(
    reviewsDue: number,
    reviewsFresh: number
): string {
    const scheduled = Math.max(0, reviewsDue - reviewsFresh)
    const segments = [
        scheduled > 0 && `${spellSmall(scheduled)} due`,
        reviewsFresh > 0 && `${spellSmall(reviewsFresh)} new`,
    ].filter(Boolean)
    return segments.length
        ? `Review · ${segments.join(' · ')}`
        : PATH_LABEL.review
}

/**
 * Composes the stand — register, date line, paragraph, and primary CTA —
 * from the dashboard loader's aggregates.
 */
export function composeStand(input: ComposeStandInput): Stand {
    const dateLine: DateLine = { primary: formatDate(input.today) }
    const done =
        input.kanjiTouched > 0 && input.kanjiTouched === input.totalRealKanji
    if (done)
        return {
            register: 'edition-complete',
            dateLine,
            primary: null,
            paragraph: [
                text(
                    'Edition One rests behind you. The way that follows — readings — is being prepared.'
                ),
            ],
        }
    // §2 trigger: when the user is in the restoration window, the patient
    // register takes precedence over `first-step` / `session` / `resting`.
    // §3.4 fourth-row case: long absence with no reviews due → return-resting.
    if (input.restoration) {
        if (input.restoration.reviewsBacklog === 0) {
            return {
                register: 'return-resting',
                dateLine,
                primary: null,
                paragraph: [
                    text(
                        'The way is patient. Nothing waits today. Begin a new lesson when you are ready.'
                    ),
                ],
            }
        }
        // De-lessoned register (restoration mini-spec): the global queue
        // has no honest single lesson, so the anchor is the queue head —
        // the first card "Ease back in" will actually serve.
        const cap = REVIEW_SITTING_SIZE_RESTORATION
        const pathLabel =
            input.restoration.reviewsBacklog >= cap
                ? `${cap} of ${input.restoration.reviewsBacklog} reviews`
                : `${input.restoration.reviewsBacklog} reviews`
        const primary: SignoffPrimary = {
            verb: 'Ease back in',
            lessonNumber: null,
            pathLabel,
            href: hrefForPath(0, 'review'),
        }
        const gathered = dueCountPhrase(input.restoration.reviewsBacklog)
        const paragraph: EntryPart[] = [
            text('The way is patient — return at the pace you can hold. '),
        ]
        if (input.restoration.anchor) {
            paragraph.push({
                kind: 'glyph',
                character: input.restoration.anchor.character,
                meaning: input.restoration.anchor.meaning,
            })
            paragraph.push(text(` waits among the ${gathered} that gathered.`))
        } else {
            paragraph.push(
                text(
                    `${gathered.charAt(0).toUpperCase()}${gathered.slice(1)} reviews gathered; they wait.`
                )
            )
        }
        return { register: 'return', dateLine, primary, paragraph }
    }
    if (!input.nextAction || !input.lesson || !input.anchor)
        return {
            register: 'resting',
            dateLine,
            primary: null,
            paragraph: [
                text(
                    'There is nothing the way asks of you today. Reviews will gather of their own accord; return when one is due.'
                ),
            ],
        }

    const { path, lessonNumber } = input.nextAction
    const primary: SignoffPrimary =
        path === 'review'
            ? {
                  verb: PATH_VERB.review,
                  lessonNumber: null,
                  pathLabel: formatReviewCountLabel(
                      input.reviewsDue,
                      input.reviewsFresh
                  ),
                  href: hrefForPath(lessonNumber, 'review'),
              }
            : {
                  verb: PATH_VERB[path],
                  lessonNumber,
                  pathLabel: PATH_LABEL[path],
                  href: hrefForPath(lessonNumber, path),
              }
    const glyph: EntryPart = {
        kind: 'glyph',
        character: input.anchor.character,
        meaning: input.anchor.meaning,
    }
    const lower = input.anchor.meaning.toLowerCase()

    if (input.kanjiTouched === 0 && input.kanjiIntroduced === 0)
        return {
            register: 'first-step',
            dateLine,
            primary,
            paragraph: [
                text('You stand at the start. Edition One opens with '),
                glyph,
                text(
                    ` — ${lower}, the first of ${compactCorpus(input.totalRealKanji)}. Begin when you are ready.`
                ),
            ],
        }

    const frameIndex = Math.max(
        1,
        input.kanjiTouched + input.kanjiIntroduced + 1
    )

    // Spec v1.6 §11.7 Decision 2: position derives from the frontier (the
    // furthest lesson actually entered), while the CTA keeps narrating the
    // recommendation cursor. When they diverge, an honest bridge sentence
    // names the cursor explicitly instead of letting the position prose
    // imply it.
    const divergent =
        input.frontierLessonNumber !== null &&
        input.frontierLessonNumber > lessonNumber
    if (divergent) {
        const bridge =
            path === 'review'
                ? ' Behind you, the reviews gathered.'
                : ` Behind you, Lesson ${lessonNumber}'s ${PATH_LABEL[path]} waits.`
        return {
            register: 'session',
            dateLine,
            primary,
            paragraph: [
                text(
                    `You stand at ${joinedFrameNumber(frameIndex)}, into Lesson ${input.frontierLessonNumber}.`
                ),
                text(bridge),
                text(" Today's frame is "),
                glyph,
                text(` — ${lower}.`),
            ],
        }
    }

    // Spec v1.7 §11.7 Decision 2: Quiz and Challenge open only once the
    // lesson's Learn is complete, so "midway through" was never true for
    // them — the lesson is walked, in the wing's own word, and its path
    // waits. Learn stands at the threshold; Review can fall due
    // mid-lesson and keeps "midway through".
    const at = `You stand at ${joinedFrameNumber(frameIndex)}`
    const position =
        path === 'learn'
            ? `${at}, at the threshold of Lesson ${input.lesson.number}.`
            : path === 'review'
              ? `${at}, midway through Lesson ${input.lesson.number}.`
              : `${at}. Lesson ${input.lesson.number} is walked; its ${PATH_LABEL[path]} waits.`
    return {
        register: 'session',
        dateLine,
        primary,
        paragraph: [
            text(position),
            text(" Today's frame is "),
            glyph,
            text(` — ${lower}.`),
        ],
    }
}
